import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  QueueStatsPollEvent,
  QueueStatsResponse,
  QueueStatsService,
} from '../../services/queue-stats.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-queue-stats',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './queue-stats.component.html',
  styleUrl: './queue-stats.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QueueStatsComponent implements OnInit, OnDestroy {
  @Input() title = 'Queue Stats';
  @Input() showDetails = false;
  @Input() compact = false;
  @Input() appearance: 'default' | 'observatory' | 'dashboard' = 'default';
  @Input() visiblePollIntervalMs = environment.QUEUE_STATS_VISIBLE_POLL_INTERVAL_MS;

  readonly initialLoadingRevealDelayMs = 180;
  readonly usePatternedDashboardShell = true;

  isLoading = true;
  isPaused = false;
  errorMessage: string | null = null;
  stats: QueueStatsResponse['data'] | null = null;
  showInitialLoadingState = false;

  private readonly queueStatsService = inject(QueueStatsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private pollSub: Subscription | null = null;
  private loadingRevealTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    if (!this.isBrowser) {
      this.mark();
      return;
    }

    this.startInitialLoadingRevealTimer();
    this.pollSub = this.queueStatsService
      .pollQueueStats(this.visiblePollIntervalMs)
      .subscribe((event) => this.handlePollEvent(event));
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
    this.clearInitialLoadingRevealTimer();
  }

  trackWorker(_: number, worker: QueueStatsResponse['data']['workers']['details'][number]): string {
    return worker.worker_id;
  }

  formatCheckedAt(epochMs: number | null | undefined): string {
    if (!epochMs) return '—';
    const date = new Date(epochMs);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
  }

  formatCheckedAtCompact(epochMs: number | null | undefined): string {
    if (!epochMs) return '—';
    const date = new Date(epochMs);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  formatSeconds(value: number | null | undefined): string {
    if (value === null || value === undefined) return 'n/a';
    if (!Number.isFinite(value)) return 'n/a';
    const totalSeconds = Math.max(0, Math.round(value));
    if (totalSeconds < 60) return `${totalSeconds}s`;

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes < 60) {
      return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  }

  formatPercent(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) return 'n/a';
    return `${Math.round(value)}%`;
  }

  formatNumber(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) return 'n/a';
    return Math.round(value).toLocaleString();
  }

  healthTone(stats: QueueStatsResponse['data']): 'healthy' | 'active' | 'warning' | 'backlogged' | 'down' {
    if (!stats.redis.ok) return 'down';

    const oldestQueued = stats.jobs.oldest_queued_age_seconds ?? 0;
    if (
      oldestQueued >= 120 ||
      stats.jobs.queue_utilization_pct >= 90 ||
      (stats.jobs.queued_count >= 10 && stats.workers.processing_count === 0)
    ) {
      return 'backlogged';
    }

    if (stats.warnings.length > 0) return 'warning';
    if (stats.jobs.queued_count > 0 || stats.jobs.processing_count > 0) return 'active';
    return 'healthy';
  }

  healthBadgeLabel(stats: QueueStatsResponse['data']): string {
    switch (this.healthTone(stats)) {
      case 'down':
        return 'Degraded';
      case 'backlogged':
        return 'Backlogged';
      case 'warning':
        return 'Warning';
      case 'active':
        return 'Active';
      default:
        return 'Healthy';
    }
  }

  healthDescription(stats: QueueStatsResponse['data']): string {
    switch (this.healthTone(stats)) {
      case 'down':
        return 'Redis is not reporting healthy. Queue telemetry may be incomplete until connectivity is restored.';
      case 'backlogged':
        return 'Queue delay is elevated or workers are not keeping up with incoming jobs.';
      case 'warning':
        return 'The queue is online, but operational warnings need attention.';
      case 'active':
        return 'Workers are actively processing jobs and queue pressure looks manageable.';
      default:
        return 'Workers are available and the queue is keeping up with demand.';
    }
  }

  workerStatusClass(status: string | null | undefined): string {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'processing') return 'worker-status-processing';
    if (normalized === 'idle') return 'worker-status-idle';
    return 'worker-status-other';
  }

  displayWorkerId(workerId: string | null | undefined): string {
    if (!workerId) return '—';
    const parts = workerId.split(':').filter(Boolean);
    return parts.at(-1) || workerId;
  }

  private handlePollEvent(event: QueueStatsPollEvent): void {
    if (event.type === 'paused') {
      this.isPaused = true;
      this.isLoading = !this.stats;
      this.mark();
      return;
    }

    if (event.type === 'error') {
      this.isPaused = false;
      this.isLoading = !this.stats;
      if (!this.stats) {
        this.revealInitialLoadingState();
      }
      this.errorMessage = this.describePollError(event.error);
      this.mark();
      return;
    }

    const response = event.response;
    if (!response?.success) {
      this.isPaused = false;
      this.isLoading = !this.stats;
      if (!this.stats) {
        this.revealInitialLoadingState();
      }
      this.errorMessage = response?.error || 'Queue stats request failed.';
      this.mark();
      return;
    }

    this.stats = response.data;
    this.isPaused = false;
    this.isLoading = false;
    this.errorMessage = null;
    this.clearInitialLoadingRevealTimer();
    this.mark();
  }

  private startInitialLoadingRevealTimer(): void {
    this.clearInitialLoadingRevealTimer();
    this.loadingRevealTimer = setTimeout(() => {
      this.showInitialLoadingState = true;
      this.loadingRevealTimer = null;
      this.mark();
    }, this.initialLoadingRevealDelayMs);
  }

  private clearInitialLoadingRevealTimer(): void {
    if (!this.loadingRevealTimer) return;
    clearTimeout(this.loadingRevealTimer);
    this.loadingRevealTimer = null;
  }

  private revealInitialLoadingState(): void {
    this.clearInitialLoadingRevealTimer();
    this.showInitialLoadingState = true;
  }

  private describePollError(error: unknown): string {
    const maybeError = error as {
      status?: number;
      message?: string;
      error?: { error?: string; message?: string };
    };
    if (maybeError?.status === 401 || maybeError?.status === 403) {
      return 'Not authorized to view queue stats. Please sign in.';
    }

    if (maybeError?.status === 503) {
      return 'Queue stats temporarily unavailable. Retrying with backoff...';
    }

    return (
      maybeError?.error?.error ||
      maybeError?.error?.message ||
      maybeError?.message ||
      'Failed to refresh queue stats. Retrying with backoff...'
    );
  }

  private mark(): void {
    this.cdr.markForCheck();
  }
}
